import React, { useState } from "react";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import PortalAwareDraggable from "./PortalAwareDraggable";
import "./EditQuestion.css";

const ALL_QUESTION_TYPES = [
  "Single-choice",
  "Multiple-choice",
  "Open-ended",
  "Yes/No",
  "Checkbox",
  "Matrix",
  "Agreement-scale",
  "Effectiveness-scale",
  "Satisfaction-scale",
  "Frequency-scale",
  "Quality-scale",
  "Likelihood-scale",
  "Importance-scale",
  "Ease-scale",
  "Value-scale",
  "Helpfulness-scale",
  "Clarity-scale",
];

const typesThatNeedOptions = [
  "Single-choice",
  "Multiple-choice",
  "Checkbox",
  "Yes/No",
  "Matrix",
  "Agreement-scale",
  "Effectiveness-scale",
  "Satisfaction-scale",
  "Frequency-scale",
  "Quality-scale",
  "Likelihood-scale",
  "Importance-scale",
  "Ease-scale",
  "Value-scale",
  "Helpfulness-scale",
  "Clarity-scale",
];

export default function EditQuestion({ surveyId, question, onUpdate, onClose }) {
  // Local state for question text and type.
  const [questionText, setQuestionText] = useState(question?.questionText || "");
  const [questionType, setQuestionType] = useState(question?.questionType || "Single-choice");

  // Normalize existing answerOptions.
  const initialOptions = (question?.answerOptions || []).map((opt) => ({
    optionText: opt.optionText || "",
    isQualify: !!opt.isQualify,
  }));
  const [answerOptions, setAnswerOptions] = useState(initialOptions);

  // Toggle qualifying question.
  const [isQualifyingQuestion, setIsQualifyingQuestion] = useState(!!question?.isQualifyingQuestion);
  const [error, setError] = useState("");

  // Validate inputs.
  const validateChanges = () => {
    if (!questionText.trim()) {
      setError("Question text cannot be empty.");
      return false;
    }
    if (
      typesThatNeedOptions.includes(questionType) &&
      answerOptions.some((opt) => !opt.optionText.trim())
    ) {
      setError("Answer options cannot have empty values.");
      return false;
    }
    return true;
  };

  // Save changes.
  const handleSave = () => {
    setError("");
    if (!validateChanges()) return;
    const updatedQuestion = {
      ...question,
      questionText,
      questionType,
      answerOptions,
      isQualifyingQuestion,
    };
    onUpdate(updatedQuestion);
    onClose();
  };

  // Handlers for answer options.
  const handleAddOption = () => {
    setAnswerOptions((prev) => [...prev, { optionText: "", isQualify: false }]);
  };

  const handleRemoveOption = (index) => {
    setAnswerOptions((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleOptionChange = (index, value) => {
    setAnswerOptions((prev) => {
      const updated = [...prev];
      updated[index].optionText = value;
      return updated;
    });
  };

  const handleToggleQualifyOption = (index) => {
    setAnswerOptions((prev) => {
      const updated = [...prev];
      updated[index].isQualify = !updated[index].isQualify;
      return updated;
    });
  };

  // Drag-and-drop reordering.
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(answerOptions);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setAnswerOptions(reordered);
  };

  const showAnswerOptions = typesThatNeedOptions.includes(questionType);

  return (
    <>
      {/* Modal overlay to close the modal when clicking outside */}
      <div className="modal-overlay" onClick={onClose} />

      <div className="edit-question-box">
        <div className="edit-question-header">
          <h2>Edit Question</h2>
          <button className="close-button" onClick={onClose}>✖</button>
        </div>

        <div className="edit-question-content">
          {error && <p className="error-message">{error}</p>}

          {/* Question Text Input */}
          <div className="form-group">
            <label>Question Text</label>
            <input
              type="text"
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="Enter the question text"
            />
          </div>

          {/* Question Type Selector */}
          <div className="form-group">
            <label>Question Type</label>
            <select
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value)}
            >
              {ALL_QUESTION_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Qualifying Question Toggle */}
          <div className="form-group qualifying-toggle">
            <label>
              <input
                type="checkbox"
                checked={isQualifyingQuestion}
                onChange={(e) => setIsQualifyingQuestion(e.target.checked)}
              />{" "}
              Mark this as a qualifying question
            </label>
          </div>

          {/* Answer Options */}
          {showAnswerOptions && (
            <div className="form-group">
              <label>Answer Options</label>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="answerOptions">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="answer-options">
                      {answerOptions.map((option, index) => (
                        <PortalAwareDraggable key={`option-${index}`} draggableId={`option-${index}`} index={index}>
                          <div className="option-item">
                            <div className="drag-handle-container">
                              <i className="fa-light fa-bars drag-handle" aria-hidden="true" />
                            </div>
                            <input
                              type="text"
                              value={option.optionText}
                              onChange={(e) => handleOptionChange(index, e.target.value)}
                              placeholder={`Option ${index + 1}`}
                            />
                            <div className="option-actions">
                              {isQualifyingQuestion && (
                                <button
                                  type="button"
                                  className={`qualify-toggle-button ${option.isQualify ? "qualified" : ""}`}
                                  onClick={() => handleToggleQualifyOption(index)}
                                  title="Mark/unmark as qualifying"
                                >
                                  Q
                                </button>
                              )}
                              <button
                                type="button"
                                className="remove-option"
                                onClick={() => handleRemoveOption(index)}
                                title="Remove this option"
                              >
                                <i className="fa-regular fa-delete-left"></i>
                              </button>
                            </div>
                          </div>
                        </PortalAwareDraggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <button type="button" className="add-option" onClick={handleAddOption}>
                + Add Option
              </button>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button className="save-button" onClick={handleSave}>Save Changes</button>
            <button className="cancel-button" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </>
  );
}
